import createDebug from 'debug';
import { RetryPromiseWithBackoff } from './RetryPromiseWithBackoff';
import { logErrorExternally } from '@/functions';
import { HttpStatus, Time } from '@/constants';

const debug = createDebug('durable:RetryableVideoChunkUpload');

/**
 * @typedef {
 *   import('./RawChunkUploadPreSignedUrls').RawChunkUploadPreSignedUrls
 * } RawChunkUploadPreSignedUrls
 */

/**
 * @param {...*} args
 */
const logError = (...args) => {
  console.error('RetryableVideoChunkUpload:', ...args);
};

/**
 * Keep track of chunk URLs that keep timing out.
 *
 * If a chunk keeps failing, we will eventually just run it without a timeout so that
 * a user with a bad connection will not have a completely stuck upload.
 *
 * @type {Map<string, number>}
 */
const timeOutsByURL = new Map();

/**
 * Number of timeouts a particular chunk URL may hit before we stop adding the timeout
 *
 * After hitting the threshold, the chunk request will just play out like normal (without
 * a timeout).
 */
const TIMEOUT_COUNT_THRESHOLD = 3;

/**
 * Create a promise for an XHR request with event, error, and success handling
 *
 * @template {unknown} T
 * @param {string} method the HTTP method to use
 * @param {string|URL} url the url to send the request to
 * @param {Parameters<XMLHttpRequest['send']>[0]} payload the payload to send
 * @param {(xhr: XMLHttpRequest) => T} onSuccess a callback to run when the request is
 *   successful. Must take an XHR as an argument and return a value to return from this promise
 * @returns {Promise<T>} a promise that resolves with the value returned by onSuccess
 */
const runXhr = (method, url, payload, onSuccess) => {
  const methodDebug = debug.extend('runXhr');
  return new Promise((resolve, reject) => {
    methodDebug('Starting');
    let madeProgress = false;
    /** @type {ReturnType<typeof setTimeout>|null} */
    let progressTimeout = null;
    const xhr = new XMLHttpRequest();

    // This listens to data upload progress events and is added before opening the request
    xhr.upload.addEventListener('progress', (event) => {
      methodDebug('Upload made progress', event);
      madeProgress = true;
    });

    methodDebug('Opening XHR', { method, url });
    xhr.open(method, url);

    /**
     * For abort handling, following approach like
     * https://github.com/aws-samples/amazon-s3-multipart-upload-transfer-acceleration/blob/main/frontendV2/src/utils/upload.js#L268
     */
    const abortXhr = () => xhr.abort();

    const listenForOffline = () => {
      window.addEventListener('offline', abortXhr);
    };
    const unlistenForOffline = () => {
      window.removeEventListener('offline', abortXhr);
    };

    methodDebug('Attaching XHR event listeners');
    xhr.onreadystatechange = () => {
      methodDebug('Ready state changed', { readyState: xhr.readyState });

      if (xhr.readyState !== XMLHttpRequest.DONE) {
        // Ignore requests that are not done
        methodDebug('XHR request not done');
        return;
      }

      // we finished our request before our progress timeout even fired,
      // so just cleanup after ourselves...
      if (progressTimeout) {
        clearTimeout(progressTimeout);
      }

      const wasRequestSuccessful =
        xhr.status >= HttpStatus.OK && xhr.status < HttpStatus.MULTIPLE_CHOICES;
      if (!wasRequestSuccessful) {
        logError('XHR request error', xhr);
        reject(xhr);
        unlistenForOffline();
        return;
      }

      try {
        methodDebug('XHR request success', xhr);
        const successValue = onSuccess(xhr);
        methodDebug('XHR finished', { successValue });
        resolve(successValue);
      } catch (error) {
        logError('Error running XHR onSuccess callback', error);
        reject(error);
      }

      unlistenForOffline();
    };

    xhr.onerror = (progressEvent) => {
      methodDebug('Encountered error');
      reject(new Error('XHR request error'));
      abortXhr();
      unlistenForOffline();
    };

    xhr.ontimeout = () => {
      methodDebug('Encountered timeout');
      reject(new Error('XHR request timeout'));
      abortXhr();
      unlistenForOffline();
    };

    xhr.onabort = () => {
      methodDebug('Encountered abort');
      reject(new Error('XHR request abort'));
      unlistenForOffline();
    };

    listenForOffline();
    methodDebug('Sending XHR');
    xhr.send(payload);
    methodDebug('Starting request progress timeout');
    const initialTimeOutCount = Number(timeOutsByURL.get(url.toString()) ?? 0);
    if (initialTimeOutCount < TIMEOUT_COUNT_THRESHOLD) {
      progressTimeout = setTimeout(() => {
        progressTimeout = null;
        if (!madeProgress) {
          console.warn('Canceling chunk upload: no progress made before timeout');
          const timeOutCount = Number(timeOutsByURL.get(url.toString()) ?? 0);
          timeOutsByURL.set(url.toString(), timeOutCount + 1);
          abortXhr();
        }
      }, Time.ONE_SECOND_IN_MS * 2);
    } else {
      console.warn(
        'Skipping adding a timeout for this chunk request as it hit the retry threshold',
      );
    }
  });
};

/**
 * Handles uploading a chunk of video by fetching a pre-signed url from VES and uploading the video
 * chunk to S3. Includes retry logic.
 */
export class RetryableVideoChunkUpload {
  rawChunkUploadPreSignedUrl = '';

  /** @type {PerformanceMark|null} */
  startMark = null;

  /** @type {PerformanceMark|null} */
  endMark = null;

  /** @type {Promise<void>|null} */
  setPreSignedUrlPromise = null;

  /** @type {null|string} */
  ETag = null;

  /**
   *
   * @param {Blob} videoChunk
   * @param {string} videoId
   * @param {number} rawChunkNumber
   * @param {RawChunkUploadPreSignedUrls} rawChunkUploadPreSignedUrls
   */
  constructor(videoChunk, videoId, rawChunkNumber, rawChunkUploadPreSignedUrls) {
    this.videoChunk = videoChunk;
    this.videoId = videoId;
    this.rawChunkNumber = rawChunkNumber;
    this.rawChunkUploadPreSignedUrls = rawChunkUploadPreSignedUrls;
  }

  /**
   * Get a pre-signed url for uploading a video chunk and upload the video chunk to it, including
   * retry logic
   *
   * @returns {Promise<this>} The chunk
   */
  async upload() {
    const methodDebug = debug.extend('upload');
    if (this.ETag) {
      methodDebug('Chunk already uploaded, returning early');
      return this;
    }

    try {
      this.startMark = performance.mark('chunk-start');
      const retryWithBackoff = new RetryPromiseWithBackoff();
      const etag = await retryWithBackoff.run(async () => {
        methodDebug('Await the chunk URL');
        await this.setPreSignedUrl();
        methodDebug('Start uploading video chunk');
        return this.uploadVideoChunk();
      });
      methodDebug('Finished uploading video chunk', { etag });
      this.endMark = performance.mark('chunk-end');
      this.ETag = etag;
      return this;
    } catch (error) {
      logError('Error uploading video chunk, out of retries', error);
      throw error;
    }
  }

  /**
   * Retrieve the presigned URL and set the property
   *
   * External users should call `setPreSignedUrl()`
   *
   * @protected
   */
  async retrieveAndSetPreSignedUrl() {
    const methodDebug = debug.extend('retrieveAndSetPreSignedUrl');
    try {
      const { rawChunkNumber } = this;
      const chunkUrl = await this.rawChunkUploadPreSignedUrls.getUrlForRawChunkNumber(
        rawChunkNumber,
      );
      methodDebug('rawChunkNumber has this chunkUrl', { rawChunkNumber, chunkUrl });
      this.rawChunkUploadPreSignedUrl = chunkUrl;
    } catch (error) {
      logError('Error setting pre-signed chunk upload url', error);
      this.setPreSignedUrlPromise = null;
      throw error;
    }
  }

  /**
   * Fetch a pre-signed url for uploading a video chunk and set it for use during upload
   */
  async setPreSignedUrl() {
    const methodDebug = debug.extend('setPreSignedUrl');
    if (this.rawChunkUploadPreSignedUrl !== '') {
      methodDebug('chunk url already exists');
      return;
    }

    if (!this.setPreSignedUrlPromise) {
      this.setPreSignedUrlPromise = this.retrieveAndSetPreSignedUrl();
    }
    await this.setPreSignedUrlPromise;
  }

  /**
   * Upload a video chunk to the pre-signed url
   *
   * @returns {Promise<string>} promise for the ETag of the video chunk
   */
  async uploadVideoChunk() {
    try {
      const methodDebug = debug.extend('uploadVideoChunk');

      if (!this.rawChunkUploadPreSignedUrl) {
        methodDebug('Pre-signed url is undefined, so we cannot upload the chunk');
        throw new Error('Pre-signed url is undefined');
      }

      /** @type {(xhr: XMLHttpRequest) => string} */
      const onSuccessfulUpload = (xhr) => {
        try {
          const successCallbackDebug = debug.extend('onSuccessfulUpload');
          const etag = xhr.getResponseHeader('ETag')?.replaceAll('"', '') || 'unknown';

          if (etag === 'unknown') {
            successCallbackDebug('Chunk ETag is unknown', xhr);
            throw new Error('Chunk ETag is unknown');
          }

          successCallbackDebug('Got an ETag for the chunk', { etag });
          return etag;
        } catch (responseError) {
          logError('Error getting ETag for chunk', responseError);
          throw responseError;
        }
      };

      const xhrPromise = /** @type {Promise<string>} */ (
        runXhr('PUT', this.rawChunkUploadPreSignedUrl, this.videoChunk, onSuccessfulUpload)
      );

      methodDebug('Awaiting chunk etag promise');
      const etag = await xhrPromise;
      methodDebug('Chunk etag promise resolved', { etag });
      return etag;
    } catch (error) {
      logErrorExternally('Error uploading video chunk', error, 'RetryableVideoChunkUpload');
      throw error;
    } finally {
      timeOutsByURL.clear();
    }
  }
}
